import { useState, useRef, useEffect } from 'react';
import { Building2, Check, ChevronDown } from 'lucide-react';
import { useActiveCompany } from '@/context/active-company-context';
import { cn } from '@/lib/utils';

// Lets a multi-company collaborator switch the active company. Hidden entirely
// for single-company users (the vast majority). Mirrors UserMenu's click-outside
// dropdown pattern.
export function CompanySwitcher() {
  const { companies, activeCompany, activeCompanyId, isMultiCompany, setActiveCompany } =
    useActiveCompany();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isMultiCompany) return null;

  const handleSelect = (companyId: number) => {
    setOpen(false);
    if (companyId !== activeCompanyId) {
      setActiveCompany(companyId);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-sm transition-colors hover:bg-accent"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Empresa activa: ${activeCompany?.name ?? 'ninguna'}. Cambiar empresa`}
      >
        <Building2 className="h-4 w-4 text-muted-foreground" />
        <span className="hidden max-w-[10rem] truncate text-foreground sm:block">
          {activeCompany?.name ?? 'Empresa'}
        </span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-56 rounded-lg border border-border bg-popover p-1 shadow-lg"
        >
          <p className="px-3 py-1.5 text-xs font-medium text-muted-foreground">Empresa activa</p>
          {companies.map((company) => {
            const isActive = company.company_id === activeCompanyId;
            return (
              <button
                key={company.company_id}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => handleSelect(company.company_id)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-sm',
                  'text-popover-foreground hover:bg-accent',
                )}
              >
                <span className="truncate">{company.name}</span>
                {isActive && <Check className="h-4 w-4 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
